import type { RouteLocationRaw } from 'vue-router';
import type { RoleName } from '@/permissions';

export interface ProductTourStep {
    id: string;
    title: string;
    body: string;
    target: string;
    align: 'start' | 'end';
    route?: RouteLocationRaw;
}

export const PRODUCT_TOUR_WELCOME = {
    title: 'Welcome to the FORTH Creditor Portal',
    body: "Take a quick tour to see where to find the information and tools you'll use most.",
};

const SHARED_STEPS: readonly ProductTourStep[] = [
    {
        id: 'dashboard',
        title: 'Your dashboard',
        body: 'See account totals, cleared transactions, work queue activity, and daily trends at a glance.',
        target: 'nav-dashboard',
        align: 'start',
    },
    {
        id: 'date-range',
        title: 'Choose your date range',
        body: 'Use this filter to update the data displayed across your dashboard.',
        target: 'dashboard-date-range',
        align: 'end',
    },
    {
        id: 'payments',
        title: 'Payments',
        body: 'View and filter transactions by bank, trust account, status, or date.',
        target: 'nav-payments',
        align: 'start',
    },
    {
        id: 'export',
        title: 'Export your data',
        body: 'Download the information in your current view whenever you need it.',
        target: 'payments-export',
        align: 'end',
        route: { name: 'payments' },
    },
    {
        id: 'reports',
        title: 'Reports',
        body: 'Explore more detailed reporting, including your Portfolio Risk view.',
        target: 'nav-reports',
        align: 'start',
    },
    {
        id: 'account',
        title: 'My Account',
        body: 'Update your profile, set your time zone, and manage your devices.',
        target: 'account',
        align: 'end',
    },
];

const ADMIN_STEPS: readonly ProductTourStep[] = [
    {
        id: 'settings',
        title: 'Your admin tools',
        body: 'Manage your company profile, banking, and team members from here.',
        target: 'settings',
        align: 'end',
    },
    {
        id: 'notifications',
        title: 'Stay informed',
        body: 'Check here for updates and alerts about your account.',
        target: 'notifications',
        align: 'end',
    },
];

const HELP_STEP: ProductTourStep = {
    id: 'help',
    title: 'Need help?',
    body: 'Find guides, training videos, and support resources here.',
    target: 'help',
    align: 'end',
};

export function getProductTourSteps(roleName: RoleName): readonly ProductTourStep[] {
    return roleName === 'admin' || roleName === 'superadmin'
        ? [...SHARED_STEPS, ...ADMIN_STEPS, HELP_STEP]
        : [...SHARED_STEPS, HELP_STEP];
}
